import * as React from "react"

import { ScrollArea as BaseScrollArea } from "@base-ui/react/scroll-area"
import { cx } from "@iyk/music-ui"

function ScrollArea({
  className,
  children,
  orientation,
  viewportRef,
  scrollGradient,
  id,
  ...props
}: React.ComponentProps<typeof BaseScrollArea.Root> & {
  orientation?: "horizontal" | "vertical"
  viewportRef?: React.RefObject<HTMLDivElement | null>
  scrollGradient?: {
    height: `${number}px`
    fromColor: {
      light: `var(--${string})`
      dark: `var(--${string})`
    }
  }
}) {
  return (
    <BaseScrollArea.Root data-slot="scroll-area" className={cx("relative", className?.toString())} {...props}>
      <BaseScrollArea.Viewport
        data-id={id && `${id}-viewport`}
        data-slot="scroll-area-viewport"
        style={
          scrollGradient
            ? ({
                "--scroll-gradient-height": scrollGradient.height,
                "--scroll-gradient-from-color-light": scrollGradient.fromColor.light,
                "--scroll-gradient-from-color-dark": scrollGradient.fromColor.dark
              } as React.CSSProperties)
            : {}
        }
        className={cx(
          "focus-visible:ring-grayA-3 size-full overscroll-contain rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline",
          scrollGradient && [
            // Top gradient
            "before:content-[''] before:absolute before:inset-x-0 before:top-0",
            "before:pointer-events-none before:rounded-[inherit]",
            "before:[--scroll-area-overflow-y-start:inherit]",
            "before:h-[min(var(--scroll-gradient-height),var(--scroll-area-overflow-y-start))]",
            "before:bg-gradient-to-b before:to-transparent",
            "before:transition-[height] before:duration-100 before:ease-out before:z-10",
            "before:from-[var(--scroll-gradient-from-color-light)] dark:before:from-[var(--scroll-gradient-from-color-dark)]",
            // Bottom gradient
            "after:content-[''] after:absolute after:inset-x-0 after:bottom-0",
            "after:pointer-events-none after:rounded-[inherit]",
            "after:[--scroll-area-overflow-y-end:inherit]",
            "after:h-[min(var(--scroll-gradient-height),var(--scroll-area-overflow-y-end,var(--scroll-gradient-height)))]",
            "after:bg-gradient-to-t after:to-transparent",
            "after:transition-[height] after:duration-100 after:ease-out after:z-10",
            "after:from-[var(--scroll-gradient-from-color-light)] dark:after:from-[var(--scroll-gradient-from-color-dark)]"
          ]
        )}
        ref={viewportRef}
      >
        {children}
      </BaseScrollArea.Viewport>
      <ScrollBar orientation={orientation} data-id={id && `${id}-scrollbar`} />
      <BaseScrollArea.Corner />
    </BaseScrollArea.Root>
  )
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof BaseScrollArea.Scrollbar>) {
  return (
    <BaseScrollArea.Scrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cx(
        "m-0.5 flex touch-none p-px opacity-0 transition-[colors,opacity] delay-200 select-none data-[hovering]:opacity-100 data-[hovering]:delay-0 data-[hovering]:duration-100 data-[scrolling]:opacity-100 data-[scrolling]:delay-0 data-[scrolling]:duration-100",
        orientation === "vertical" && "w-2 border-l border-l-transparent",
        orientation === "horizontal" && "h-2 flex-col border-t border-t-transparent",
        className?.toString()
      )}
      {...props}
    >
      <BaseScrollArea.Thumb
        data-slot="scroll-area-thumb"
        className="bg-grayA-3 relative flex-1 rounded-full z-10"
      />
    </BaseScrollArea.Scrollbar>
  )
}

export { ScrollArea }
